import { LoadingButton } from "@mui/lab";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { setCompetitorAnalysis } from "../../../../redux/slices/competitorSlice";
import {
  useGetMoreKeywordsByAnalysisIdMutation,
  useReinitializeKeywordsByAnalysisIdMutation,
} from "../../../../redux/slices/postEditorBlogSlice";
import { dispatch } from "../../../../redux/store";
import {
  getText,
  removeAccents,
} from "../../../../pages/postEditor/seoAnalyzer/helper";
import { keywordCount } from "../../../../pages/postEditor/seoAnalyzer/semanticHelper";
import { useEditorContext } from "../../../../contexts/EditorContext";
import debounce from "lodash/debounce";

export const SemanticsCompetitorAnalysis = ({
  competitorResults,
  showModal,
  showModalHandle,
  formikProps,
}) => {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState([]);
  const [keywordCounts, setKeywordCounts] = useState({});
  const editor = useEditorContext();

  // Combine all text for counting
  const textContent = useMemo(() => {
    return (
      getText(formikProps.values.introduction.trim()).toLowerCase().trim() +
      getText(formikProps.values.content.trim()).toLowerCase().trim() +
      getText(formikProps.values.conclusion.trim()).toLowerCase().trim()
    );
  }, [
    formikProps.values.introduction,
    formikProps.values.content,
    formikProps.values.conclusion,
  ]);

  // Total word count in editor
  const wordCount = useMemo(() => {
    return editor?.getText().trim()
      ? editor
          ?.getText()
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length
      : 0;
  }, [editor?.state]);

  // Count a keyword in the current text
  const handleCountKeyword = useCallback((keyword) => {
    return keywordCount(removeAccents(keyword), removeAccents(textContent));
  }, [textContent]);

  // Highlight keywords in editor
  const handleHighlightWords = useCallback(() => {
    if (editor && keywords.length > 0) {
      const sortedKeywords = [...keywords].sort(
        (a, b) => b.keywords.length - a.keywords.length
      );

      editor.commands.toggleHighlighting(true);
      editor.commands.addKeywords(sortedKeywords.map((item) => item.keywords));

      const colors = sortedKeywords.map((item) => {
        const count = handleCountKeyword(item.keywords);
        return count === 0
          ? "bg-[#D6D3D1]"
          : count < Math.round((wordCount * item.low) / 100)
          ? "bg-cyan-900/50"
          : count > Math.round((wordCount * item.high) / 100)
          ? "bg-red-700/50"
          : "bg-lime-700/50";
      });

      editor.commands.addColors(colors);
      editor.commands.highlightKeywords();
    } else if (editor) {
      // Clear highlights if no keywords
      editor.commands.toggleHighlighting(false);
      editor.commands.addKeywords([]);
      editor.commands.addColors([]);
      editor.commands.highlightKeywords();
    }
  }, [editor, keywords, wordCount, handleCountKeyword]);

  // Calculate all keyword counts
  const calculateKeywordCounts = useCallback(() => {
    const counts = {};

    if (competitorResults?.keywords_analysis?.secondary_keywords) {
      competitorResults.keywords_analysis.secondary_keywords.forEach((item) => {
        counts[item.keywords] = handleCountKeyword(item.keywords);
      });
    }

    if (competitorResults?.keywords) {
      competitorResults.keywords.forEach((kw) => {
        counts[kw] = handleCountKeyword(kw);
      });
    }

    setKeywordCounts(counts);
  }, [competitorResults, handleCountKeyword]);

  // Debounced version: highlighting + counting
  const debouncedUpdate = useMemo(
    () => debounce(() => {
      handleHighlightWords();
      calculateKeywordCounts();
    }, 1000),
    [handleHighlightWords, calculateKeywordCounts]
  );

  // Run debounced update whenever text, keywords, or editor changes
  useEffect(() => {
    if (!editor) return;
    debouncedUpdate();
    return () => debouncedUpdate.cancel();
  }, [keywords, textContent, wordCount, editor, debouncedUpdate]);

  // Toggle keyword selection
  const handleKeyword = (item) => {
    setKeywords((prev) =>
      prev.some((i) => i.keywords === item.keywords)
        ? prev.filter((i) => i.keywords !== item.keywords)
        : [...prev, item]
    );
  };

  // Show modal
  const handleShowModal = () => {
    showModalHandle({
      keywords: competitorResults?.keywords_analysis,
      show: true,
      type: "semantics",
      semantics_view: competitorResults?.semantics_view,
    });
  };

  // API mutations
  const [loadKeywords, { isLoading: keywordLoading }] =
    useGetMoreKeywordsByAnalysisIdMutation();
  const [reinitalizeKeywords, { isLoading: reinitalizeKeywordsLoading }] =
    useReinitializeKeywordsByAnalysisIdMutation();

  const handleShowMoreKeywords = async (id, limit) => {
    const res = await loadKeywords({ seo_analysis_id: id, limit }).unwrap();
    if (res?.nb_keywords) {
      dispatch(
        setCompetitorAnalysis({
          data: {
            ...competitorResults,
            keywords: [...competitorResults?.keywords, ...res.keywords],
            nb_keywords: res?.nb_keywords,
          },
        })
      );
    }
  };

  const handleReinitializeKeywords = async (id) => {
    const res = await reinitalizeKeywords({ seo_analysis_id: id }).unwrap();
    if (res?.nb_keywords) {
      dispatch(
        setCompetitorAnalysis({
          data: {
            ...competitorResults,
            keywords: res.keywords,
            nb_keywords: res?.nb_keywords,
          },
        })
      );
    }
  };

  return (
    <div className="mb-8">
      {/* Competitor Analysis */}
      {competitorResults?.keywords_analysis?.secondary_keywords?.length > 0 && (
        <>
          <p className="text-white text-xl mt-6 mb-4 font-bold">
            {t("Secondary-keywords", "Secondary Keywords")}
          </p>

          <div className="flex gap-3 items-center flex-wrap text-white">
            {competitorResults.keywords_analysis.secondary_keywords.map((item) => {
              const count = keywordCounts[item.keywords] ?? 0;
              const min = Math.round((item.low / 100) * wordCount);
              const max = Math.round((item.high / 100) * wordCount);

              const keywordBg =
                count === 0
                  ? keywords.some((i) => i.keywords === item.keywords)
                    ? "bg-[#b1b1b1]"
                    : "bg-[#D6D3D1]"
                  : count < min
                  ? keywords.some((i) => i.keywords === item.keywords)
                    ? "bg-[#598495]"
                    : "bg-[#8AA6B1]"
                  : count > max
                  ? keywords.some((i) => i.keywords === item.keywords)
                    ? "bg-[#ad7c7a]"
                    : "bg-[#DF9290]"
                  : keywords.some((i) => i.keywords === item.keywords)
                  ? "bg-[#849966]"
                  : "bg-[#A6BD87]";

              return (
                <div key={item.keywords} className="flex items-stretch gap-0">
                  <div
                    onClick={() => handleKeyword(item)}
                    className={`rounded-l font-bold px-3 py-2 text-xs text-black cursor-pointer drop-shadow-xl flex items-center ${keywordBg}`}
                  >
                    {item.keywords}
                  </div>

                  <div className="rounded-r px-3 py-2 text-xs flex items-center gap-1 bg-black/40 text-white">
                    <span className="text-sm font-semibold">{count}</span>
                    <span className="text-[11px]">/ {min}-{max}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {!showModal && (
            <div className="text-center mt-5">
              <button
                className="bg-blue-200 hover:bg-blue-100 px-5 py-[6px] rounded text-sm text-white"
                onClick={handleShowModal}
              >
                {t("See-the-full-repartition", "See the full repartition")}
              </button>
            </div>
          )}
        </>
      )}

      {/* Additional Keywords */}
      {competitorResults?.keywords?.length > 0 && (
        <>
          <p className="text-white text-xl mt-6 mb-4 font-bold">
            {t("Additional-keywords", "Additional Keywords")}
          </p>

          <div className="flex gap-3 items-center flex-wrap">
            {competitorResults.keywords.map((item) => {
              const count = keywordCounts[item] ?? 0;

              return (
                <div key={item} className="flex items-stretch gap-0">
                  <div
                    className={`rounded-l font-bold px-3 py-2 text-xs text-black flex items-center ${
                      count > 0 ? "bg-green-200" : "bg-stone-300"
                    }`}
                  >
                    {item}
                  </div>

                  <div className="rounded-r px-3 py-2 text-xs font-normal bg-black/40 text-white flex items-center">
                    {count}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-5 flex items-center justify-center gap-2 text-white">
            {competitorResults?.has_more_keywords && (
              <LoadingButton
                variant="contained"
                loading={keywordLoading}
                className="!text-sm hover:!bg-blue-100 !bg-blue-200 !text-white font-light px-5 py-[6px] rounded flex items-center !normal-case"
                onClick={() =>
                  handleShowMoreKeywords(
                    competitorResults.id,
                    competitorResults.nb_keywords
                  )
                }
              >
                {t("See-more-keywords", "See more keywords")}
              </LoadingButton>
            )}
            {competitorResults?.keywords?.length > 20 && (
              <LoadingButton
                variant="contained"
                loading={reinitalizeKeywordsLoading}
                className="!text-sm !bg-slate-100 hover:!bg-blue-100 hover:!text-white !text-black font-light px-5 py-[6px] rounded flex items-center !capitalize"
                onClick={() => handleReinitializeKeywords(competitorResults.id)}
              >
                {t("Re-initalize", "Re-initalize")}
              </LoadingButton>
            )}
          </div>
        </>
      )}
    </div>
  );
};
